/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/prefer-default-export */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

import useToast from '../../../hooks/useToast';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { getWeeklyWorkReport } from '../../../services/workReport';
import { filtersAdapter } from '../../../adapters/weekly-report/filters-adapter';
import { throttle } from '../../../utils/globalFunctions';

/** @typedef {import('../../../adapters/weekly-report/get-weekly-report-adapter').WeeklyWorkReport} WeeklyWorkReport */

/** @type {WeeklyWorkReport} */
const initialStateWeeklyReportData = {
  pagination: { pageIndex: 1, totalCount: 0, totalPages: 0 },
  tableData: []
};

/**
 * @typedef UseTable
 * @property {import("../hooks/useFilters").Filters} filters
 * @property {boolean} isInspector
 * @property {import("./useSort").SortType} sortType
 * @property {Date} selectedDay
 */

const areDatesValid = (filters) => filters.fechaDesde && filters.fechaHasta;

const areGroupsSame = (lastGroupValue, dataValue) => {
  const lastGroupFirstColumn = lastGroupValue?.group[0]?.firstColumn;
  const dataGroupFirstColumn = dataValue.tableData[0]?.group[0]?.firstColumn;

  return (
    lastGroupFirstColumn?.agrupadoPor === dataGroupFirstColumn?.agrupadoPor &&
    lastGroupFirstColumn?.titulo === dataGroupFirstColumn?.titulo
  );
};

const shouldShowHeader = (secondColumn, lastSecondColumn) =>
  secondColumn.agrupadoPor !== lastSecondColumn.agrupadoPor ||
  secondColumn.nroSolicitud !== lastSecondColumn.nroSolicitud;

const processGroupData = (lastGroupValue, dataValue) => {
  dataValue.tableData[0]?.group?.forEach(({ secondColumn, weeklyReport }) => {
    const lastSecondColumn = lastGroupValue?.group?.at(-1).secondColumn;

    const newSecondColumn = shouldShowHeader(secondColumn, lastSecondColumn)
      ? secondColumn
      : { ...secondColumn, showHeader: false };

    lastGroupValue?.group?.push({ secondColumn: newSecondColumn, weeklyReport });
  });
};

const getObserverOptions = (pagination, isFetchingNextPage) => ({
  root: document.querySelector('#table-container'),
  rootMargin: '500px',
  stopObserve: pagination.pageIndex === pagination.totalPages || isFetchingNextPage
});

const useTableObserver = (observerRef, tableData, isLoading, pagination, isFetchingNextPage) => {
  const observerOptions = getObserverOptions(pagination, isFetchingNextPage);

  return useIntersectionObserver(
    tableData.length && !isLoading ? observerRef : null,
    observerOptions
  );
};

/**  @param {UseTable} */
export const useTable = ({ filters, isInspector, sortType, selectedDay }) => {
  /** @type {[WeeklyWorkReport, React.Dispatch<React.SetStateAction<WeeklyWorkReport>>]} */
  const [weeklyReportData, setWeeklyReportData] = useState(initialStateWeeklyReportData);

  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [isLoading, setIsLoading] = useState(false);

  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  /** @type {[Boolean, React.Dispatch<React.SetStateAction<Boolean>>]} */
  const [isInitialRender, setIsInitialRender] = useState(true);

  const { pagination, tableData } = weeklyReportData;

  const startDay = selectedDay || filters?.fechaDesde;

  /** @type {import('react').Ref<Element>} */
  const observerRef = useRef(null);
  const entry = useTableObserver(observerRef, tableData, isLoading, pagination, isFetchingNextPage);

  const { setNewToast } = useToast();

  useEffect(() => {
    // Create cancelation token
    const source = axios.CancelToken.source();

    setWeeklyReportData(initialStateWeeklyReportData);
    if (!areDatesValid(filters)) return;
    setIsLoading(true);
    getWeeklyWorkReport({
      fechaSeleccionada: startDay,
      sortType,
      filtros: filtersAdapter(filters, isInspector),
      isInspector,
      cancelToken: source
    })
      .then((data) => {
        setWeeklyReportData(data);
        setIsLoading(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setNewToast({ type: 'ERROR', message: error.message });
          setIsLoading(false);
        }
      })
      .finally(() => {
        setIsInitialRender(false);
      });

    // When rerenders we cancel the previous fetch to avoid wrong data rendering
    // eslint-disable-next-line consistent-return
    return () => source.cancel('Request canceled');
  }, [filters, selectedDay, sortType]);

  const updateState = (stateValue, lastGroupValue, dataValue) => ({
    ...stateValue,
    pagination: {
      ...stateValue.pagination,
      pageIndex: stateValue.pagination.pageIndex + 1
    },
    tableData: stateValue.tableData
      .slice(0, stateValue.tableData.length - 1)
      .concat(lastGroupValue)
      .concat(dataValue.tableData.slice(1))
  });

  const fetchNextPage = useCallback(() => {
    setIsFetchingNextPage(true);
    getWeeklyWorkReport({
      fechaSeleccionada: startDay,
      sortType,
      pageNumber: pagination.pageIndex + 1,
      filtros: filtersAdapter(filters, isInspector),
      isInspector
    })
      .then((data) =>
        setWeeklyReportData((state) => {
          const lastGroup = state.tableData[state.tableData.length - 1];

          if (data.tableData.length && areGroupsSame(lastGroup, data)) {
            processGroupData(lastGroup, data);

            return updateState(state, lastGroup, data);
          }

          return {
            ...state,
            pagination: { ...state.pagination, pageIndex: state.pagination.pageIndex + 1 },
            tableData: state.tableData.concat(data.tableData)
          };
        })
      )
      .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
      .finally(() => setIsFetchingNextPage(false));
  }, [pagination.pageIndex, setNewToast, sortType, startDay, filters]);

  const fetchNextPageThrottle = useCallback(throttle(fetchNextPage), [
    startDay,
    pagination.pageIndex,
    sortType,
    filters
  ]);

  const reloadTable = useCallback(() => {
    setWeeklyReportData(initialStateWeeklyReportData);
    setIsLoading(true);
    getWeeklyWorkReport({
      fechaSeleccionada: startDay,
      sortType,
      filtros: filtersAdapter(filters, isInspector),
      isInspector
    })
      .then((data) => setWeeklyReportData(data))
      .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
      .finally(() => setIsLoading(false));
  }, [setNewToast, sortType, startDay, filters]);

  const handleDisableDay = useCallback(
    /** @param {string} id  */
    (id) => {
      const [groupIndex, lineIndex, dayIndex] = id.split('-');

      const tableDataCopy = structuredClone(tableData);

      tableDataCopy[+groupIndex].group[+lineIndex].weeklyReport[+dayIndex].habilitaCheck = false;

      const isDisabledLine = tableDataCopy[+groupIndex].group[+lineIndex].weeklyReport.every(
        (workReport) => !workReport?.habilitaCheck
      );

      tableDataCopy[+groupIndex].group[+lineIndex].secondColumn.isDisabled = isDisabledLine;

      const isDisabledGroup = tableDataCopy[+groupIndex].group.every(
        (positionLine) => positionLine.secondColumn.isDisabled
      );

      tableDataCopy[+groupIndex].group[0].firstColumn.isDisabled = isDisabledGroup;

      setWeeklyReportData((state) => ({ ...state, tableData: tableDataCopy }));
    },
    [tableData]
  );

  useEffect(() => {
    if (entry?.isIntersecting && pagination.pageIndex < pagination.totalPages) {
      fetchNextPageThrottle();
    }
  }, [entry?.isIntersecting]);

  return {
    // fetchAllData,
    handleDisableDay,
    isFetchingNextPage,
    isInitialRender,
    isLoading,
    observerRef,
    reloadTable,
    tableData,
    updateApprovedOrRejectedCells: reloadTable
  };
};
